import React from 'react';
import { colors, surfaces, textColors, typography, borders, spacing, borderRadius, shadows } from '../../tokens';

/**
 * TokenShowcase
 * Displays all design tokens with visual examples
 * Theme-aware: works in both light and dark modes
 */
const TokenShowcase = () => {
  return (
    <div className="space-y-12">
      {/* Theme-Aware Surfaces Section */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-6`}>Theme-Aware Surfaces</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          These surfaces automatically adapt between light and dark themes. Always use these instead of hardcoded colors.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries({
            'Primary': surfaces.primary,
            'Secondary': surfaces.secondary,
            'Tertiary': surfaces.tertiary,
            'Elevated': surfaces.elevated,
          }).map(([name, classes]) => (
            <div key={name} className={`${classes} p-6 rounded-lg border ${borders.default}`}>
              <h3 className={`${typography.h5} ${textColors.primary} mb-2`}>{name} Surface</h3>
              <p className={`${typography.bodySmall} ${textColors.secondary} mb-2`}>
                Used for {name.toLowerCase()} content areas
              </p>
              <code className={`text-xs ${textColors.muted} block bg-gray-100 dark:bg-dark-800 p-2 rounded`}>
                {classes}
              </code>
            </div>
          ))}
        </div>
      </section>

      {/* Theme-Aware Text Colors Section */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-6`}>Text Color Scale</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          Six-level text color scale that provides proper contrast in both light and dark modes.
        </p>
        
        <div className={`${surfaces.primary} p-6 rounded-lg border ${borders.default} space-y-3`}>
          <div>
            <span className={`${textColors.brightest} font-semibold`}>Brightest Text</span>
            <span className={`${typography.caption} ${textColors.muted} ml-3`}>- Hero headlines, maximum contrast</span>
          </div>
          <div>
            <span className={`${textColors.bright} font-semibold`}>Bright Text</span>
            <span className={`${typography.caption} ${textColors.muted} ml-3`}>- Important headings (h1, h2)</span>
          </div>
          <div>
            <span className={`${textColors.primary} font-semibold`}>Primary Text</span>
            <span className={`${typography.caption} ${textColors.muted} ml-3`}>- Standard headings, card titles</span>
          </div>
          <div>
            <span className={`${textColors.secondary}`}>Secondary Text</span>
            <span className={`${typography.caption} ${textColors.muted} ml-3`}>- Body text, most readable content</span>
          </div>
          <div>
            <span className={`${textColors.tertiary}`}>Tertiary Text</span>
            <span className={`${typography.caption} ${textColors.muted} ml-3`}>- Supporting text, metadata</span>
          </div>
          <div>
            <span className={`${textColors.muted}`}>Muted Text</span>
            <span className={`${typography.caption} ${textColors.muted} ml-3`}>- Hints, placeholders, disabled</span>
          </div>
        </div>
      </section>

      {/* Color Palette Section */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-6`}>Color Palette</h2>
        
        {/* Primary Colors */}
        <div className="mb-8">
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>Primary (Blue)</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-10 gap-3">
            {Object.entries(colors.primary).map(([shade, color]) => (
              <div key={shade} className="text-center">
                <div 
                  className={`h-16 rounded-lg shadow-sm mb-2 border ${borders.light}`}
                  style={{ backgroundColor: color }}
                  title={color}
                />
                <div className={`text-xs font-medium ${textColors.secondary}`}>{shade}</div>
                <div className={`text-xs ${textColors.muted} font-mono`}>{color}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Accent Colors */}
        <div className="mb-8">
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>Accent (Green)</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-10 gap-3">
            {Object.entries(colors.accent).map(([shade, color]) => (
              <div key={shade} className="text-center">
                <div 
                  className={`h-16 rounded-lg shadow-sm mb-2 border ${borders.light}`}
                  style={{ backgroundColor: color }}
                  title={color}
                />
                <div className={`text-xs font-medium ${textColors.secondary}`}>{shade}</div>
                <div className={`text-xs ${textColors.muted} font-mono`}>{color}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Neutral Colors */}
        <div className="mb-8">
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>Neutral (Gray)</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-10 gap-3">
            {Object.entries(colors.neutral).map(([shade, color]) => (
              <div key={shade} className="text-center">
                <div 
                  className={`h-16 rounded-lg shadow-sm mb-2 border ${borders.light}`}
                  style={{ backgroundColor: color }}
                  title={color}
                />
                <div className={`text-xs font-medium ${textColors.secondary}`}>{shade}</div>
                <div className={`text-xs ${textColors.muted} font-mono`}>{color}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Semantic Colors */}
        <div className="mb-8">
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>Semantic Colors</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(colors.semantic).map(([name, shades]) => (
              <div key={name}>
                <h4 className={`text-sm font-medium ${textColors.secondary} mb-2 capitalize`}>{name}</h4>
                <div className="space-y-2">
                  {Object.entries(shades).map(([shade, color]) => (
                    <div key={shade} className="flex items-center gap-2">
                      <div 
                        className={`w-16 h-10 rounded shadow-sm border ${borders.light}`}
                        style={{ backgroundColor: color }}
                      />
                      <div>
                        <div className={`text-xs font-medium ${textColors.secondary}`}>{shade}</div>
                        <div className={`text-xs ${textColors.muted} font-mono`}>{color}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Gradients */}
        <div className="mb-8">
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>Gradients</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(colors.gradients).map(([name, gradient]) => (
              <div key={name}>
                <div 
                  className="h-24 rounded-lg shadow-md mb-2"
                  style={{ background: gradient.css }}
                />
                <div className={`text-sm font-medium ${textColors.primary} capitalize`}>{name}</div>
                <code className={`text-xs ${textColors.muted} break-all`}>{gradient.css}</code>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Typography Section */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-6`}>Typography</h2>
        
        {/* Typography Presets */}
        <div className={`${surfaces.primary} p-6 rounded-lg border ${borders.default} space-y-6`}>
          <div>
            <div className={`${typography.caption} ${textColors.muted} mb-2`}>Heading 1</div>
            <h1 className={`${typography.h1} ${textColors.brightest}`}>The quick brown fox jumps over the lazy dog</h1>
          </div>
          <div>
            <div className={`${typography.caption} ${textColors.muted} mb-2`}>Heading 2</div>
            <h2 className={`${typography.h2} ${textColors.bright}`}>The quick brown fox jumps over the lazy dog</h2>
          </div>
          <div>
            <div className={`${typography.caption} ${textColors.muted} mb-2`}>Heading 3</div>
            <h3 className={`${typography.h3} ${textColors.primary}`}>The quick brown fox jumps over the lazy dog</h3>
          </div>
          <div>
            <div className={`${typography.caption} ${textColors.muted} mb-2`}>Heading 4</div>
            <h4 className={`${typography.h4} ${textColors.primary}`}>The quick brown fox jumps over the lazy dog</h4>
          </div>
          <div>
            <div className={`${typography.caption} ${textColors.muted} mb-2`}>Heading 5</div>
            <h5 className={`${typography.h5} ${textColors.secondary}`}>The quick brown fox jumps over the lazy dog</h5>
          </div>
          <div>
            <div className={`${typography.caption} ${textColors.muted} mb-2`}>Body Text (Large)</div>
            <p className={`${typography.bodyLarge} ${textColors.secondary}`}>The quick brown fox jumps over the lazy dog</p>
          </div>
          <div>
            <div className={`${typography.caption} ${textColors.muted} mb-2`}>Body Text (Regular)</div>
            <p className={`${typography.body} ${textColors.secondary}`}>The quick brown fox jumps over the lazy dog</p>
          </div>
          <div>
            <div className={`${typography.caption} ${textColors.muted} mb-2`}>Body Text (Small)</div>
            <p className={`${typography.bodySmall} ${textColors.secondary}`}>The quick brown fox jumps over the lazy dog</p>
          </div>
          <div>
            <div className={`${typography.caption} ${textColors.muted} mb-2`}>Muted Text</div>
            <p className={`${typography.muted} ${textColors.muted}`}>The quick brown fox jumps over the lazy dog</p>
          </div>
        </div>
      </section>

      {/* Spacing Section */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-6`}>Spacing Scale</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          Based on Tailwind's 4px base unit. Common values shown:
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {[0, 1, 2, 3, 4, 6, 8, 12, 16, 20, 24, 32].map((size) => (
            <div key={size} className="text-center">
              <div 
                className="bg-blue-500 dark:bg-blue-400 rounded mb-2 mx-auto" 
                style={{ width: spacing[size], height: spacing[size] }} 
              />
              <div className={`text-sm font-medium ${textColors.primary}`}>{size}</div>
              <div className={`text-xs ${textColors.muted}`}>{spacing[size]}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Border Radius Section */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-6`}>Border Radius</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(borderRadius).filter(([name]) => !name.includes('xl')).map(([name, value]) => (
            <div key={name} className="text-center">
              <div 
                className={`w-24 h-24 bg-blue-500 dark:bg-blue-400 mx-auto mb-2`}
                style={{ borderRadius: value }}
              />
              <div className={`text-sm font-medium ${textColors.primary}`}>{name}</div>
              <div className={`text-xs ${textColors.muted}`}>{value}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Shadows Section */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-6`}>Shadows</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Object.entries(shadows).filter(([name]) => name !== '2xl').map(([name, shadow]) => (
            <div key={name}>
              <div 
                className={`${surfaces.primary} rounded-lg p-6 mb-2`}
                style={{ boxShadow: shadow }}
              >
                <div className={`text-sm font-medium ${textColors.primary}`}>Shadow: {name}</div>
              </div>
              <code className={`text-xs ${textColors.muted} block`}>{shadow}</code>
            </div>
          ))}
        </div>
      </section>

      {/* Borders Section */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-6`}>Theme-Aware Borders</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries({
            'Default': borders.default,
            'Light': borders.light,
            'Strong': borders.strong,
            'Focus': borders.focus,
          }).map(([name, classes]) => (
            <div key={name} className={`${surfaces.primary} p-6 rounded-lg border-2 ${classes}`}>
              <h3 className={`${typography.h5} ${textColors.primary} mb-2`}>{name} Border</h3>
              <code className={`text-xs ${textColors.muted}`}>{classes}</code>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default TokenShowcase;
